<template>
  <div class="app-container patrol-monitor">
    <!-- 页头：标题 + 数据更新时间 + 全局动作 -->
    <div class="page-header">
      <div class="page-title">
        <h2>巡店异常监控</h2>
        <span class="update-badge" v-if="overview.dt">
          <i class="el-icon-calendar"></i> 数据日期 {{ overview.dt }}
        </span>
        <span class="update-badge is-empty" v-else>等待首轮轮询…</span>
      </div>
      <div class="page-actions">
        <el-button size="small" icon="el-icon-refresh" :loading="overviewLoading" @click="refreshAll">刷新</el-button>
        <el-button type="primary" size="small" icon="el-icon-video-play" :loading="runLoading" @click="handleRun">立即轮询一轮</el-button>
      </div>
    </div>

    <!-- 系统健康条：凭据 / 调度 / 最近执行 -->
    <el-card shadow="never" class="health-card" v-loading="overviewLoading">
      <div class="health-items">
        <div class="health-item">
          <span class="dot" :class="'dot-' + credDotClass"></span>
          <span class="health-label">采集凭据</span>
          <span class="health-value" :class="'text-' + credDotClass">{{ credSummary }}</span>
        </div>
        <el-divider direction="vertical" />
        <div class="health-item">
          <span class="dot" :class="overview.schedule && overview.schedule.enabled ? 'dot-success' : 'dot-info'"></span>
          <span class="health-label">定时轮询</span>
          <span class="health-value">
            {{ overview.schedule && overview.schedule.enabled ? '运行中' : '未启用' }}
            <span class="health-sub" v-if="overview.schedule && overview.schedule.cron">（{{ overview.schedule.cron }}）</span>
          </span>
        </div>
        <el-divider direction="vertical" />
        <div class="health-item">
          <span class="health-label">最近执行</span>
          <span class="health-value" v-if="overview.schedule && overview.schedule.lastRunAt">
            {{ $getDateTime(overview.schedule.lastRunAt) }}
            <el-tag v-if="overview.schedule.lastStatus" size="mini"
              :type="overview.schedule.lastStatus === 'success' ? 'success' : 'danger'">
              {{ overview.schedule.lastStatus === 'success' ? '成功' : '失败' }}
            </el-tag>
            <span class="health-sub" v-if="storePushSummary">（{{ storePushSummary }}）</span>
          </span>
          <span class="health-value is-empty" v-else>暂无执行记录</span>
        </div>
      </div>
    </el-card>

    <!-- 业务统计：类别接口驱动（动态新增自动出现，如 RevenueEx）；本地 TONE_MAP 仅配色/静默覆盖 -->
    <el-row :gutter="12" class="ex-row" v-loading="overviewLoading">
      <el-col :span="4" :xs="12" v-for="ex in exRows" :key="ex.exCode">
        <div class="ex-card" :class="'ex-' + ex.tone" :style="{ '--ex-color': ex.color }">
          <div class="ex-head">
            <span class="ex-name">{{ ex.exName }}</span>
            <span class="ex-kind" :class="ex.kindIsEvent ? 'is-event' : 'is-status'">{{ ex.kindLabel }}</span>
          </div>
          <div class="ex-total">
            <span class="num">{{ ex.total === null ? '—' : ex.total }}</span>
            <span class="unit">家</span>
          </div>
          <div class="ex-sub">入库 {{ ex.recordCount }} 条</div>
        </div>
      </el-col>
    </el-row>

    <!-- 播报配置 -->
    <el-card shadow="never" class="section-card">
      <div slot="header" class="card-header">
        <span>播报配置</span>
        <span class="header-tip">事件类新增推门店明细（统一群 + 开启开关的店铺群）、状态类计数变化播报（统一群）</span>
      </div>
      <el-form :inline="true" @submit.native.prevent>
        <el-form-item label="播报开关：">
          <el-switch v-model="config.enabled" />
        </el-form-item>
        <el-form-item label="企微群会话ID：">
          <el-input v-model="config.conversationId" placeholder="从企微群管理获取会话ID（wr 开头）" style="width: 320px;" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" size="small" :loading="configSaving" @click="handleSaveConfig">保存配置</el-button>
        </el-form-item>
      </el-form>
      <div v-if="runResult" class="run-result">
        <template v-if="runResult.failed">
          <el-alert type="error" :closable="false" show-icon
            :title="`轮询失败（${runResult.kind}，连续 ${runResult.streak} 轮${runResult.alerted ? '，已发告警' : ''}）：${runResult.error}`" />
        </template>
        <template v-else>
          <el-alert type="success" :closable="false" show-icon
            :title="`本轮完成（${runResult.durationMs}ms）：${runResult.messageQueued ? '已发送企微消息' : '无新增无变化，静默'}${storeRunSummary}${(runResult.failures && runResult.failures.length) ? '；部分类型失败：' + runResult.failures.length + ' 类' : ''}`" />
        </template>
      </div>
    </el-card>

    <!-- 异常记录表 -->
    <el-card shadow="never" class="section-card">
      <div slot="header" class="card-header">
        <span>异常记录</span>
        <span class="header-tip">事件类门店明细 · 同店同日同类型一条 · 保留 90 天</span>
      </div>
      <el-form :inline="true" @submit.native.prevent>
        <el-form-item label="日期：">
          <el-date-picker v-model="queryDt" type="date" value-format="yyyy-MM-dd" placeholder="全部" clearable style="width: 160px;" />
        </el-form-item>
        <el-form-item label="类型：">
          <el-select v-model="queryExCode" clearable placeholder="全部" style="width: 140px;">
            <el-option v-for="ex in exOptions" :key="ex.code" :label="ex.name" :value="ex.code" />
          </el-select>
        </el-form-item>
        <el-form-item label="关键字：">
          <el-input v-model="queryKeyword" placeholder="门店名 / 城市 / 子类型" clearable style="width: 200px;" @keyup.enter.native="fetchRecords(1)" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" size="small" icon="el-icon-search" @click="fetchRecords(1)">查询</el-button>
        </el-form-item>
      </el-form>

      <el-table :data="records.list" border stripe v-loading="recordsLoading" size="small">
        <el-table-column prop="dt" label="日期" width="100" align="center" />
        <el-table-column label="类型" width="100" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" :type="exTagType(scope.row.exCode)" effect="plain">{{ scope.row.exName }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="subExCode" label="子类型" width="120" show-overflow-tooltip />
        <el-table-column prop="wmPoiName" label="门店" min-width="190" show-overflow-tooltip />
        <el-table-column prop="cityName" label="城市" width="110" show-overflow-tooltip />
        <el-table-column prop="desc" label="异常描述" min-width="320" show-overflow-tooltip />
        <el-table-column label="首次发现" width="150" align="center">
          <template slot-scope="scope">{{ $getDateTime(scope.row.firstSeenAt) }}</template>
        </el-table-column>
        <el-table-column label="最近仍在" width="150" align="center">
          <template slot-scope="scope">{{ $getDateTime(scope.row.lastSeenAt) }}</template>
        </el-table-column>
      </el-table>
      <el-pagination style="margin-top: 12px; text-align: right;" background layout="total, prev, pager, next, jumper"
        :total="records.total" :current-page.sync="records.pagenum" :page-size="records.pagesize"
        @current-change="fetchRecords" />
    </el-card>
  </div>
</template>

<script>
// 类别配色/静默覆盖表（与后端 EX_META 对应；未覆盖的新类别走 DEFAULT_TONE 中性色+推送，
// 接口 exList 单源驱动卡片，动态新增自动出现）
const TONE_MAP = {
  BusinessEx: { name: '营业异常', tag: 'danger', tone: 'event', color: '#F56C6C' },
  CommodityEx: { name: '商品异常', tag: 'warning', tone: 'event', color: '#E6A23C', notify: false },
  RiskControlEx: { name: '风险管控', tag: 'danger', tone: 'event', color: '#CF1322' },
  CommentEx: { name: '评价异常', tag: 'info', tone: 'status', color: '#909399' },
  ActivityEx: { name: '活动异常', tag: 'info', tone: 'status', color: '#909399' },
  OperationEx: { name: '经营异常', tag: 'info', tone: 'status', color: '#909399' }
}
const DEFAULT_TONE = { tag: 'info', tone: 'status', color: '#909399', notify: true }

export default {
  name: 'PatrolMonitor',
  data () {
    return {
      overviewLoading: false,
      overview: { exList: [], credential: { status: 'none' }, schedule: null, dt: '' },
      config: { enabled: false, conversationId: '' },
      configSaving: false,
      runLoading: false,
      runResult: null,
      queryDt: '',
      queryExCode: '',
      queryKeyword: '',
      recordsLoading: false,
      records: { total: 0, pagenum: 1, pagesize: 20, list: [] }
    }
  },
  computed: {
    // 筛选下拉：内置 6 类 + 接口动态新增类别（如 RevenueEx）合并
    exOptions () {
      const base = [
        { code: 'BusinessEx', name: '营业异常' },
        { code: 'CommodityEx', name: '商品异常' },
        { code: 'CommentEx', name: '评价异常' },
        { code: 'RiskControlEx', name: '风险管控' },
        { code: 'ActivityEx', name: '活动异常' },
        { code: 'OperationEx', name: '经营异常' }
      ]
      const extra = this.overview.exList
        .filter(e => !base.some(b => b.code === e.exCode))
        .map(e => ({ code: e.exCode, name: e.exName }))
      return [...base, ...extra]
    },
    // 统计卡行：接口 exList 驱动 + 本地配色/静默覆盖；未轮询时兑底展示内置 6 类（数值 —）
    exRows () {
      const source = this.overview.exList.length
        ? this.overview.exList.map(e => ({
          exCode: e.exCode,
          exName: e.exName,
          total: e.total,
          recordCount: e.recordCount,
          kind: e.kind,
          notify: e.notify
        }))
        : Object.keys(TONE_MAP).map(code => ({ exCode: code, exName: '', total: null, recordCount: 0, kind: null, notify: null }))
      return source.map(e => {
        const tone = TONE_MAP[e.exCode] || DEFAULT_TONE
        const base = e.kind === 'event' ? '事件' : '状态'
        return {
          exCode: e.exCode,
          exName: e.exName || (TONE_MAP[e.exCode] && TONE_MAP[e.exCode].name) || e.exCode,
          total: e.total,
          recordCount: e.recordCount,
          tone: tone.tone,
          color: tone.color,
          kindIsEvent: base === '事件',
          kindLabel: e.notify === false ? base + '·静默' : base
        }
      })
    },
    // 凭据圆点样式类（success/warning/danger/info）
    credDotClass () {
      const c = this.overview.credential
      if (!c || c.status === 'none' || c.status === 'expired') return 'danger'
      if (c.status === 'pending') return 'info'
      return c.daysLeft !== null && c.daysLeft < 5 ? 'warning' : 'success'
    },
    // 凭据摘要文案（健康条用）
    credSummary () {
      const c = this.overview.credential
      if (!c || c.status === 'none') return '未录入'
      if (c.status === 'pending') return '待激活'
      if (c.status === 'expired') return '已过期'
      const left = c.daysLeft === 0 ? '剩不足 1 天' : `剩 ${c.daysLeft} 天`
      return `${left}（${c.name}）`
    },
    // 店铺群精确推送摘要（健康条用；最近一轮调度的 storePush 统计）
    storePushSummary () {
      const sp = this.overview.schedule && this.overview.schedule.lastResult &&
        this.overview.schedule.lastResult.storePush
      if (!sp || !sp.total) return ''
      const parts = [`店铺群${sp.pushedGroups}群/${sp.total}条`]
      if (sp.noMap) parts.push(`未匹配${sp.noMap}`)
      if (sp.switchOff) parts.push(`开关关${sp.switchOff}`)
      if (sp.noGroup) parts.push(`未绑群${sp.noGroup}`)
      if (sp.failed) parts.push(`失败${sp.failed}`)
      return parts.join('·')
    },
    // 手动轮询结果的店铺群推送摘要（run-result 提示条用）
    storeRunSummary () {
      const sp = this.runResult && this.runResult.storePush
      if (!sp || !sp.total) return ''
      return `；店铺群推送 ${sp.pushedGroups} 群/${sp.total} 条`
    }
  },
  created () {
    this.fetchOverview()
    this.fetchConfig()
    this.fetchRecords(1)
  },
  methods: {
    exTagType (code) {
      const hit = TONE_MAP[code]
      return hit ? hit.tag : 'info'
    },
    refreshAll () {
      this.fetchOverview()
      this.fetchRecords(1)
    },
    fetchOverview () {
      this.overviewLoading = true
      this.$http.get('patrol-notify/overview').then(({ data: res }) => {
        if (res.meta.status === 200) {
          this.overview = res.data
        } else {
          this.$message.error('概览加载失败：' + (res.meta.msg || res.meta.status))
        }
      }).catch(e => {
        this.$message.error('概览请求失败：' + e.message)
      }).finally(() => { this.overviewLoading = false })
    },
    fetchConfig () {
      this.$http.get('patrol-notify/config').then(({ data: res }) => {
        if (res.meta.status === 200) this.config = res.data
        else this.$message.error('配置加载失败：' + (res.meta.msg || res.meta.status))
      }).catch(e => {
        this.$message.error('配置请求失败：' + e.message)
      })
    },
    handleSaveConfig () {
      this.configSaving = true
      this.$http.post('patrol-notify/config', {
        enabled: this.config.enabled,
        conversationId: this.config.conversationId
      }).then(({ data: res }) => {
        if (res.meta.status === 200) {
          this.$message.success('配置已保存')
        } else {
          this.$message.error(res.meta.msg || '保存失败')
        }
      }).finally(() => { this.configSaving = false })
    },
    handleRun () {
      this.runLoading = true
      this.runResult = null
      this.$http.post('patrol-notify/run').then(({ data: res }) => {
        if (res.meta.status === 200) {
          // skipped：被前置条件拦下（未启用/未配群/无凭据），非本轮执行结果
          if (res.data && res.data.skipped) {
            this.$message.warning('本轮未执行：' + res.data.skipped)
            return
          }
          this.runResult = res.data
          this.fetchOverview()
          this.fetchRecords(1)
        } else {
          this.$message.error(res.meta.msg || '轮询失败')
        }
      }).finally(() => { this.runLoading = false })
    },
    fetchRecords (pagenum) {
      if (pagenum) this.records.pagenum = pagenum
      this.recordsLoading = true
      const params = {
        pagenum: this.records.pagenum,
        pagesize: this.records.pagesize
      }
      if (this.queryDt) params.dt = this.queryDt
      if (this.queryExCode) params.exCode = this.queryExCode
      if (this.queryKeyword && this.queryKeyword.trim()) params.keyword = this.queryKeyword.trim()
      this.$http.get('patrol-notify/records', { params }).then(({ data: res }) => {
        if (res.meta.status === 200) {
          this.records = res.data
        } else {
          this.$message.error('记录加载失败：' + (res.meta.msg || res.meta.status))
        }
      }).catch(e => {
        this.$message.error('记录请求失败：' + e.message)
      }).finally(() => { this.recordsLoading = false })
    }
  }
}
</script>

<style scoped>
/* ===== 设计 token ===== */
.patrol-monitor {
  --c-bg-card: #ffffff;
  --c-border: #ebeef5;
  --c-text-primary: #303133;
  --c-text-secondary: #606266;
  --c-text-muted: #909399;
  --c-success: #67c23a;
  --c-warning: #e6a23c;
  --c-danger: #f56c6c;
  --c-info: #909399;
  --shadow-hover: 0 4px 12px rgba(0, 0, 0, 0.08);
  --transition-card: transform 0.2s ease, box-shadow 0.2s ease;
}

/* ===== 页头 ===== */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.page-title {
  display: flex;
  align-items: baseline;
  gap: 12px;
}
.page-title h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--c-text-primary);
}
.update-badge {
  font-size: 12px;
  color: var(--c-text-secondary);
  background: #f0f9eb;
  border: 1px solid #e1f3d8;
  border-radius: 10px;
  padding: 2px 10px;
}
.update-badge.is-empty {
  background: #f4f4f5;
  border-color: #e9e9eb;
  color: var(--c-text-muted);
}
.page-actions {
  display: flex;
  gap: 8px;
}

/* ===== 系统健康条 ===== */
.health-card >>> .el-card__body {
  padding: 10px 20px;
}
.health-items {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 0;
}
.health-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 16px;
}
.health-item .el-divider--vertical {
  margin: 0 16px 0 0;
  height: 14px;
}
.health-label {
  font-size: 13px;
  color: var(--c-text-muted);
}
.health-value {
  font-size: 13px;
  color: var(--c-text-primary);
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.health-value.is-empty {
  color: var(--c-text-muted);
  font-weight: 400;
}
.health-sub {
  font-size: 12px;
  color: var(--c-text-muted);
  font-weight: 400;
}
.text-success { color: var(--c-success); }
.text-warning { color: var(--c-warning); }
.text-danger { color: var(--c-danger); }

/* 状态圆点（指示灯） */
.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dot-success { background: var(--c-success); box-shadow: 0 0 0 3px rgba(103, 194, 58, 0.15); }
.dot-warning { background: var(--c-warning); box-shadow: 0 0 0 3px rgba(230, 162, 60, 0.15); }
.dot-danger { background: var(--c-danger); box-shadow: 0 0 0 3px rgba(245, 108, 108, 0.15); }
.dot-info { background: var(--c-info); box-shadow: 0 0 0 3px rgba(144, 147, 153, 0.15); }

/* ===== 业务统计卡 ===== */
.ex-row {
  margin-top: 12px;
}
.ex-card {
  background: var(--c-bg-card);
  border: 1px solid var(--c-border);
  border-radius: 4px;
  padding: 14px 16px;
  position: relative;
  overflow: hidden;
  transition: var(--transition-card);
  margin-bottom: 12px;
}
.ex-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: var(--c-info);
  opacity: 0.35;
}
.ex-card.ex-event::before {
  background: var(--ex-color, var(--c-danger));
  opacity: 1;
}
.ex-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-hover);
}
.ex-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.ex-name {
  font-size: 13px;
  color: var(--c-text-secondary);
}
.ex-kind {
  font-size: 11px;
  line-height: 1;
  padding: 3px 6px;
  border-radius: 2px;
}
.ex-kind.is-event {
  color: var(--ex-color, var(--c-danger));
  background: rgba(245, 108, 108, 0.1);
}
.ex-kind.is-status {
  color: var(--c-text-muted);
  background: #f4f4f5;
}
.ex-total {
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.ex-total .num {
  font-size: 28px;
  font-weight: 600;
  line-height: 1.1;
  color: var(--c-text-primary);
  font-variant-numeric: tabular-nums;
}
.ex-card.ex-event .ex-total .num {
  color: var(--ex-color, var(--c-danger));
}
.ex-total .unit {
  font-size: 12px;
  color: var(--c-text-muted);
}
.ex-sub {
  font-size: 12px;
  color: var(--c-text-muted);
  margin-top: 6px;
  font-variant-numeric: tabular-nums;
}

/* ===== 分区卡片（配置/记录表） ===== */
.section-card {
  margin-top: 4px;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.header-tip {
  font-size: 12px;
  color: var(--c-text-muted);
  font-weight: 400;
}
.run-result {
  margin-top: 4px;
}

/* 窄屏：统计卡两列折行 */
@media (max-width: 992px) {
  .ex-row >>> .el-col {
    margin-bottom: 12px;
  }
}
</style>
